import { useEffect, useRef, useState } from "react";
import { Building2, Check, Loader2, Upload } from "lucide-react";
import { authFetch } from "../../lib/authFetch";
import { useClientAuth } from "../../lib/auth-context";

export interface CompanyProfile {
  id: string;
  companyName: string;
  siren: string | null;
  numeroClient: string | number | null;
  sector: string | null;
  adresseFacturation: unknown;
  website: string | null;
  nbSalaries: number | null;
  couleurPrimaire: string | null;
  couleurSecondaire: string | null;
  logoUrl: string | null;
  lastEditedAt?: string | null;
  lastEditedBy?: string | null;
}

const inputClass = "w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-primary/50 disabled:opacity-70";
const labelClass = "mb-1.5 block text-xs font-medium text-muted-foreground";

function ReadOnlyField({ label, value }: { label: string; value: string | number | null | undefined }) {
  return <div><p className={labelClass}>{label}</p><div className={`${inputClass} min-h-10`}>{value || "—"}</div></div>;
}

function formatBillingAddress(value: unknown): string | null {
  if (typeof value === "string") return value;
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const address = value as Record<string, unknown>;
  const text = (key: string) => typeof address[key] === "string" ? (address[key] as string).trim() : "";
  return [
    [text("numero"), text("rue") || text("adresse")].filter(Boolean).join(" "),
    [text("code_postal"), text("ville")].filter(Boolean).join(" "),
    text("pays"),
  ].filter(Boolean).join(", ") || null;
}

export default function CompanyProfileSection({ company, isAdmin, onSaved }: {
  company: CompanyProfile;
  isAdmin: boolean;
  onSaved: (company: CompanyProfile) => void;
}) {
  const { updateCompanyBrand } = useClientAuth();
  const [website, setWebsite] = useState(company.website || "");
  const [nbSalaries, setNbSalaries] = useState(company.nbSalaries?.toString() || "");
  const [primary, setPrimary] = useState(company.couleurPrimaire || "#4e63f0");
  const [secondary, setSecondary] = useState(company.couleurSecondaire || "#2facf3");
  const [logoUrl, setLogoUrl] = useState(company.logoUrl || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [notice, setNotice] = useState<{ type: "error" | "success"; text: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const savedColors = useRef({
    couleurPrimaire: company.couleurPrimaire || "#4e63f0",
    couleurSecondaire: company.couleurSecondaire || "#2facf3",
  });

  useEffect(() => {
    updateCompanyBrand({ couleurPrimaire: primary, couleurSecondaire: secondary });
  }, [primary, secondary, updateCompanyBrand]);
  useEffect(() => () => updateCompanyBrand(savedColors.current), [updateCompanyBrand]);

  const save = async () => {
    setSaving(true);
    setNotice(null);
    try {
      const response = await authFetch("/api/espace-client/my/company", {
        method: "PUT",
        body: JSON.stringify({
          website: website.trim() || null,
          nbSalaries: nbSalaries ? Number(nbSalaries) : null,
          couleurPrimaire: primary,
          couleurSecondaire: secondary,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || "Enregistrement impossible.");
      savedColors.current = { couleurPrimaire: primary, couleurSecondaire: secondary };
      const updated = { ...company, ...data, website: website.trim() || null, nbSalaries: nbSalaries ? Number(nbSalaries) : null, couleurPrimaire: primary, couleurSecondaire: secondary };
      onSaved(updated);
      updateCompanyBrand({ couleurPrimaire: primary, couleurSecondaire: secondary });
      setNotice({ type: "success", text: "Modifications enregistrées." });
    } catch (reason) {
      updateCompanyBrand(savedColors.current);
      setNotice({ type: "error", text: reason instanceof Error ? reason.message : "Enregistrement impossible." });
    } finally { setSaving(false); }
  };

  const uploadLogo = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { setNotice({ type: "error", text: "Sélectionnez un fichier image." }); return; }
    if (file.size > 2 * 1024 * 1024) { setNotice({ type: "error", text: "Le fichier doit peser 2 Mo maximum." }); return; }
    setUploading(true);
    setNotice(null);
    try {
      const body = new FormData();
      body.append("logo", file);
      const response = await authFetch("/api/espace-client/my/company/logo", { method: "PUT", body });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || "Envoi du logo impossible.");
      setLogoUrl(data.logoUrl);
      onSaved({ ...company, logoUrl: data.logoUrl });
      updateCompanyBrand({ logoUrl: data.logoUrl });
      setNotice({ type: "success", text: "Logo mis à jour." });
    } catch (reason) {
      setNotice({ type: "error", text: reason instanceof Error ? reason.message : "Envoi du logo impossible." });
    } finally { setUploading(false); }
  };

  return (
    <section className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(250px,1fr)] gap-5">
      <div className="bg-card border border-border/60 rounded-xl overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border/50">
          <Building2 className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold text-foreground">Informations de la société</h2>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><ReadOnlyField label="Raison sociale" value={company.companyName} /></div>
          <ReadOnlyField label="SIREN" value={company.siren} />
          <ReadOnlyField label="Numéro client" value={company.numeroClient} />
          <ReadOnlyField label="Secteur d’activité" value={company.sector} />
          <ReadOnlyField label="Adresse de facturation" value={formatBillingAddress(company.adresseFacturation)} />
          <label className="block">
            <span className={labelClass}>Site web</span>
            <input className={inputClass} type="url" value={website} onChange={event => setWebsite(event.target.value)} disabled={!isAdmin} placeholder="https://exemple.fr" />
          </label>
          <label className="block">
            <span className={labelClass}>Nombre de salariés</span>
            <input className={inputClass} type="number" min="0" step="1" value={nbSalaries} onChange={event => setNbSalaries(event.target.value)} disabled={!isAdmin} placeholder="Non renseigné" />
          </label>
          {company.lastEditedAt && <p className="sm:col-span-2 text-xs text-muted-foreground">Dernière modification : {new Date(company.lastEditedAt).toLocaleString("fr-FR")}{company.lastEditedBy ? ` · ${company.lastEditedBy}` : ""}</p>}
        </div>
      </div>

      <div className="bg-card border border-border/60 rounded-xl overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-border/50">
          <h2 className="text-sm font-semibold text-foreground">Logo et couleurs</h2>
        </div>
        <div className="p-5 flex-1 space-y-5">
          <div className="flex flex-col items-center gap-3">
            {logoUrl ? <img src={logoUrl} alt={`Logo de ${company.companyName}`} className="w-20 h-20 rounded-lg border border-border object-contain p-1" /> :
              <div className="w-20 h-20 rounded-lg flex items-center justify-center text-white text-xl font-semibold" style={{ backgroundColor: primary }}>{company.companyName.slice(0, 2).toUpperCase()}</div>}
            {isAdmin && <>
              <input ref={fileRef} className="sr-only" type="file" accept="image/*" onChange={uploadLogo} aria-label="Choisir une image de logo" />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-xs text-foreground hover:bg-secondary/60 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {uploading ? "Envoi…" : "Changer le logo"}
              </button>
              <p className="text-center text-xs text-muted-foreground">Image uniquement, 2 Mo maximum.</p>
            </>}
          </div>
          <div className="border-t border-border/60 pt-4 space-y-3">
            <ColorField label="Couleur principale" value={primary} disabled={!isAdmin} onChange={setPrimary} />
            <ColorField label="Couleur secondaire" value={secondary} disabled={!isAdmin} onChange={setSecondary} />
            {isAdmin && <p className="text-xs text-muted-foreground">L’aperçu de la barre latérale et des accents est mis à jour en direct.</p>}
          </div>
          {isAdmin && <button type="button" onClick={save} disabled={saving} className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-white disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2" style={{ backgroundColor: primary }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>}
          {notice && <p className={`text-xs ${notice.type === "error" ? "text-destructive" : "text-green-600"}`} role="status">{notice.text}</p>}
        </div>
      </div>
    </section>
  );
}

function ColorField({ label, value, disabled, onChange }: { label: string; value: string; disabled: boolean; onChange: (value: string) => void }) {
  return <label className="flex items-center justify-between gap-3 text-xs">
    <span className="text-foreground">{label}</span>
    <span className="flex items-center gap-2">
      <span aria-hidden="true" className="h-6 w-6 rounded border border-border" style={{ backgroundColor: value }} />
      {disabled ? <span className="font-mono text-muted-foreground">{value}</span> :
        <input type="color" aria-label={label} value={value} onChange={event => onChange(event.target.value)} className="h-8 w-10 cursor-pointer rounded border-0 bg-transparent p-0" />}
    </span>
  </label>;
}